import { get } from 'lodash';
import {
  FieldNamesMarkedBoolean,
  FieldPath,
  FieldValues
} from 'react-hook-form';

/**
 * Type-safe utility to check if a field or any of its parent fields are dirty
 *
 * @param dirtyFields - Object containing dirty field information
 * @param path - Type-safe path to the field using FieldPath
 * @returns true if the field or any of its parent fields are marked as dirty
 *
 * @example
 * ```typescript
 * const form = useForm<MyFormData>();
 *
 * // Check if specific field is dirty
 * const isNameDirty = isFieldOrParentDirty(form.formState.dirtyFields, 'user.profile.name');
 *
 * // Check if entire user object is dirty
 * const isUserDirty = isFieldOrParentDirty(form.formState.dirtyFields, 'user');
 *
 * // Check if profile object is dirty
 * const isProfileDirty = isFieldOrParentDirty(form.formState.dirtyFields, 'user.profile');
 * ```
 */
export function isFieldOrParentDirty<TFieldValues extends FieldValues>(
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<TFieldValues>>>,
  path: FieldPath<TFieldValues>
): boolean {
  const pathParts = path.split('.');

  // Check if the field itself or any of its parent fields are dirty
  for (let i = 0; i < pathParts.length; i++) {
    const currentPath = pathParts.slice(0, i + 1).join('.');
    if (get(dirtyFields, currentPath) === true) {
      return true;
    }
  }

  // Check if the field itself or any of its descendants are dirty
  const fieldValue = get(dirtyFields, path);
  return !!fieldValue;
}
